import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { useCompanies } from '@/hooks/useBackofficeData';
import type { BackofficeCompany } from '@/types/backoffice.types';
import { ROUTES } from '@/constants/routes';
import styles from './CompanyListPage.module.scss';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Texto por el que se puede buscar la columna de admin (admins, invitados o creador). */
function adminSearchText(company: BackofficeCompany): string {
  return [
    ...company.admins.flatMap((a) => [a.name, a.email]),
    ...company.pendingAdminInvites,
    company.createdBy?.name ?? '',
    company.createdBy?.email ?? '',
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Responsable de la empresa, por orden de precedencia:
 * 1. Admin(s) propios de la empresa.
 * 2. Invitación de admin enviada y aún sin aceptar.
 * 3. El usuario de backoffice que creó la empresa (empresas recién creadas, sin usuarios).
 */
function AdminCell({ company }: { company: BackofficeCompany }) {
  const [admin, ...rest] = company.admins;

  if (admin) {
    return (
      <div className={styles.companyList__stackedCell}>
        <span className={styles.companyList__primaryText}>{admin.name}</span>
        <span className={styles.companyList__secondaryText}>
          {admin.email}
          {rest.length > 0 && ` +${rest.length}`}
        </span>
      </div>
    );
  }

  const [invited, ...moreInvited] = company.pendingAdminInvites;
  if (invited) {
    return (
      <div className={styles.companyList__stackedCell}>
        <Badge variant="warning">Invitación pendiente</Badge>
        <span className={styles.companyList__secondaryText}>
          {invited}
          {moreInvited.length > 0 && ` +${moreInvited.length}`}
        </span>
      </div>
    );
  }

  if (company.createdBy) {
    return (
      <div className={styles.companyList__stackedCell}>
        <Badge variant="info">Backoffice</Badge>
        <span className={styles.companyList__secondaryText}>{company.createdBy.email}</span>
      </div>
    );
  }

  return <Badge variant="default">Sin admin</Badge>;
}

export function CompanyListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const { companies, isLoading } = useCompanies();

  const filteredCompanies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return companies;
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(query) ||
        company.code.toLowerCase().includes(query) ||
        adminSearchText(company).includes(query)
    );
  }, [companies, searchQuery]);

  const columns: DataTableColumn<BackofficeCompany>[] = [
    {
      key: 'name',
      header: 'Empresa',
      accessor: (row) => (
        <div className={styles.companyList__stackedCell}>
          <span className={styles.companyList__primaryText}>{row.name}</span>
          <span className={styles.companyList__secondaryText}>{row.code}</span>
        </div>
      ),
      width: '30%',
    },
    {
      key: 'admin',
      header: 'Admin',
      accessor: (row) => <AdminCell company={row} />,
      width: '30%',
    },
    {
      key: 'employees',
      header: 'Alumnos',
      accessor: (row) => row.totalEmployees,
      sortable: true,
      align: 'right',
    },
    {
      key: 'createdAt',
      header: 'Creada',
      accessor: (row) => formatDate(row.createdAt),
      align: 'right',
    },
  ];

  const handleRowClick = (company: BackofficeCompany) => {
    navigate(`${ROUTES.BACKOFFICE.COMPANIES}/${company.id}`);
  };

  return (
    <div className={styles.companyList}>
      <div className={styles.companyList__header}>
        <div>
          <h1>Empresas</h1>
          <p className={styles.companyList__subtitle}>Gestiona todas las cuentas de empresa</p>
        </div>
        <Button leftIcon={<Plus />} onClick={() => navigate(`${ROUTES.BACKOFFICE.COMPANIES}/new`)}>
          Crear empresa
        </Button>
      </div>

      <Card className={styles.companyList__filters}>
        <Input
          type="search"
          placeholder="Buscar por nombre, código o admin..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Card>

      <DataTable
        columns={columns}
        data={filteredCompanies}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        emptyMessage="No se encontraron empresas"
      />
    </div>
  );
}

export default CompanyListPage;
